/**
 * Fully self-driven replacement for the native <product-recommendations>
 * fetch behavior, used for the complementary -> related fallback.
 *
 * Background: this theme compiles its JS (including the
 * product-recommendations custom element class) into a single built
 * main.js bundle at theme-build time, so editing
 * assets/product-recommendations.js directly has no effect on the live
 * site. On top of that, the frozen native class only ever fetches ONCE,
 * the first time its element is connected to the DOM - if Shopify later
 * patches the SAME element's data-product-id/data-url attributes in
 * place (e.g. after adding another item to the cart, which changes
 * cart.items.last), the native class does nothing, so the block silently
 * keeps showing recommendations for the previous product.
 *
 * This script takes over the fetching entirely instead of trying to
 * detect/race the frozen native class:
 *   - Fetches the primary (data-url) recommendations itself, immediately
 *     (no artificial delay), for every <product-recommendations
 *     data-url="..."> element - both ones present at page load and ones
 *     that appear later (AJAX cart drawer / cart page re-renders).
 *   - If that returns 0 products and a data-fallback-url is present,
 *     immediately fetches the fallback (related) instead.
 *   - Watches data-product-id for changes (via MutationObserver on
 *     attributes, not just childList) so that when the cart's last item
 *     changes and Shopify patches the element in place, this re-fetches
 *     right away for the new product - clearing the stale content first
 *     so old products are never shown against the new cart state.
 *
 * Registered once, globally, from a real <script src> tag in
 * layout/theme.liquid, so it is part of every full page load and
 * unaffected by any individual section's innerHTML being swapped out.
 */
(function () {
  var STATE_ATTR = 'data-fallback-state';

  function fetchInner(url, productId) {
    return fetch(url + '&product_id=' + productId)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var tmpl = document.createElement('template');
        tmpl.innerHTML = html;
        return tmpl.content.querySelector('product-recommendations');
      });
  }

  function process(el) {
    var url = el.getAttribute('data-url');
    var fallbackUrl = el.getAttribute('data-fallback-url');
    var productId = el.getAttribute('data-product-id');
    if (!url || !productId) return;

    // Already fetched/rendered for this exact product - nothing to do.
    if (el.getAttribute(STATE_ATTR) === productId) return;
    el.setAttribute(STATE_ATTR, productId);

    // Clear immediately so a stale previous product's cards are never
    // shown while the request for the new one is in flight.
    el.innerHTML = '';

    fetchInner(url, productId)
      .then(function (inner) {
        // The product changed again while this request was in flight -
        // drop the now-stale result, a newer call will handle it.
        if (el.getAttribute('data-product-id') !== productId) return null;
        if (inner && inner.hasChildNodes()) {
          el.innerHTML = inner.innerHTML;
          return null;
        }
        if (!fallbackUrl) return null;
        return fetchInner(fallbackUrl, productId);
      })
      .then(function (inner) {
        if (!inner) return;
        if (el.getAttribute('data-product-id') !== productId) return;
        if (inner.hasChildNodes()) {
          el.innerHTML = inner.innerHTML;
        }
      })
      .catch(function (error) {
        console.log(error); // eslint-disable-line
      });
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches && root.matches('product-recommendations[data-url]')) {
      process(root);
    }
    if (root.querySelectorAll) {
      var nodes = root.querySelectorAll('product-recommendations[data-url]');
      for (var i = 0; i < nodes.length; i++) {
        process(nodes[i]);
      }
    }
  }

  function start() {
    scan(document.body);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'childList') {
          for (var j = 0; j < m.addedNodes.length; j++) {
            scan(m.addedNodes[j]);
          }
        } else if (m.type === 'attributes' && m.target.nodeType === 1) {
          if (m.target.matches && m.target.matches('product-recommendations[data-url]')) {
            process(m.target);
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-product-id', 'data-url', 'data-fallback-url']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
